import type { WorkExperienceCompany, WorkExperienceRole } from '@applicator/shared'

import {
  applyWorkExperienceGroupMode,
  type WorkExperienceGroupMode,
} from './workExperienceGroup'

export type { WorkExperienceGroupMode } from './workExperienceGroup'
export type WorkExperienceSortMode = 'manual' | 'by_date'

export type WorkExperienceDisplayOptions = {
  sortMode: WorkExperienceSortMode
  groupMode: WorkExperienceGroupMode
}

const PRESENT_LABELS = new Set(['present', 'настоящее время', 'current'])

/** Deep clone companies for manual-order backup. */
export function cloneWorkExperienceCompanies(
  companies: WorkExperienceCompany[]
): WorkExperienceCompany[] {
  return JSON.parse(JSON.stringify(companies)) as WorkExperienceCompany[]
}

function normalizeDateToken(value?: string): string {
  return (value ?? '').trim()
}

function isPresentDate(value?: string): boolean {
  const v = normalizeDateToken(value).toLowerCase()
  return PRESENT_LABELS.has(v) || v.includes('present')
}

/**
 * Parse role date for sorting (MM/YYYY from Teal editor, or DD/MM/YYYY).
 * Returns timestamp; missing dates sort oldest (0).
 */
export function parseWorkExperienceDateForSort(
  dateStr: string | undefined,
  options?: { treatAsEnd?: boolean }
): number {
  const raw = normalizeDateToken(dateStr)
  if (!raw || raw === 'Not set' || raw === 'N/A') return 0
  if (isPresentDate(raw)) {
    return options?.treatAsEnd ? Date.now() : Date.now()
  }

  const monthYear = raw.match(/^(\d{1,2})\/(\d{4})$/)
  if (monthYear) {
    const month = parseInt(monthYear[1], 10) - 1
    const year = parseInt(monthYear[2], 10)
    if (Number.isNaN(month) || Number.isNaN(year)) return 0
    const day = options?.treatAsEnd ? 28 : 1
    return new Date(year, month, day).getTime()
  }

  const parts = raw.split('/')
  if (parts.length === 3) {
    const day = parseInt(parts[0], 10)
    const month = parseInt(parts[1], 10) - 1
    const year = parseInt(parts[2], 10)
    if (Number.isNaN(day) || Number.isNaN(month) || Number.isNaN(year)) return 0
    return new Date(year, month, day).getTime()
  }

  const parsed = Date.parse(raw)
  return Number.isNaN(parsed) ? 0 : parsed
}

function compareRolesByDate(a: WorkExperienceRole, b: WorkExperienceRole): number {
  const endA = parseWorkExperienceDateForSort(a.endDate, { treatAsEnd: true })
  const endB = parseWorkExperienceDateForSort(b.endDate, { treatAsEnd: true })
  if (endB !== endA) return endB - endA

  const startA = parseWorkExperienceDateForSort(a.startDate)
  const startB = parseWorkExperienceDateForSort(b.startDate)
  if (startB !== startA) return startB - startA

  return (a.position ?? '').localeCompare(b.position ?? '')
}

function companySortScore(company: WorkExperienceCompany): number {
  const roles = company.roles ?? []
  if (roles.length === 0) return 0
  return Math.max(
    ...roles.map((role) => parseWorkExperienceDateForSort(role.endDate, { treatAsEnd: true }))
  )
}

function compareCompaniesByDate(a: WorkExperienceCompany, b: WorkExperienceCompany): number {
  const scoreB = companySortScore(b)
  const scoreA = companySortScore(a)
  if (scoreB !== scoreA) return scoreB - scoreA

  return (a.name ?? '').localeCompare(b.name ?? '')
}

/** Newest roles first within each company; newest companies first. */
export function sortWorkExperienceByDate(
  companies: WorkExperienceCompany[]
): WorkExperienceCompany[] {
  return [...companies]
    .map((company) => ({
      ...company,
      roles: [...(company.roles ?? [])].sort(compareRolesByDate),
    }))
    .sort(compareCompaniesByDate)
}

/** Apply group (company vs position) then optional date sort. */
export function applyWorkExperienceDisplayOrder(
  companies: WorkExperienceCompany[],
  { sortMode, groupMode }: WorkExperienceDisplayOptions
): WorkExperienceCompany[] {
  const grouped = applyWorkExperienceGroupMode(companies, groupMode)
  if (sortMode === 'by_date') {
    return sortWorkExperienceByDate(grouped)
  }
  return grouped
}

/** After edits in position mode: merge, re-split so multi-role rows become separate blocks. */
export function normalizeWorkExperienceForPositionMode(
  companies: WorkExperienceCompany[]
): WorkExperienceCompany[] {
  return applyWorkExperienceGroupMode(companies, 'position')
}
